import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, FormControl, InputLabel, Select,
  MenuItem, Button, CircularProgress, Alert, Pagination, Tooltip, Tabs, Tab
} from '@mui/material';
import {
  ManageHistory as ActivityIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon
} from '@mui/icons-material';
import api from '../services/api';

const ACTION_COLOR = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'error',
  STATUS_CHANGE: 'warning',
  PASSWORD_RESET: 'default',
};

const PAGE_LABELS = {
  '/':                  'Dashboard',
  '/content':           'Content Library',
  '/content/new':       'Add Content',
  '/content/audit':     'Content Audit Log',
  '/user-activity':     'User Activity Log',
  '/bulk-upload':       'Bulk Upload',
  '/social-media':      'Social Media',
  '/users':             'User Management',
  '/settings':          'Configure',
  '/manager':           'Manager Dashboard',
  '/employee':          'Employee Dashboard',
  '/director':          'Director Dashboard',
  '/editor':            'Editor Dashboard',
  '/expenses':          'Expense Management',
  '/todos':             'Todo App',
  '/awards':            'Awards & Recognition',
  '/attendance':        'Attendance',
  '/inventory':         'Inventory Management',
  '/recruitment':       'Recruitment',
  '/leave-calendar':    'Leave Calendar',
  '/profit-distribution': 'Profit Distribution',
  '/salary':            'Employee Salary',
  '/notifications':     'Notifications',
};

function pageName(path) {
  if (!path) return '—';
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith('/content/edit/')) return 'Edit Content';
  return path;
}

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

// ── Login Activity Tab ────────────────────────────────────────────────────────
function LoginActivityTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ username: '', success: '', startDate: '', endDate: '' });

  const load = useCallback(async (pg = 1) => {
    setLoading(true); setError('');
    try {
      const params = { page: pg, limit: 50 };
      if (filters.username)  params.username  = filters.username;
      if (filters.success === 'resume') {
        params.success = 'true';
        params.failureReason = 'Session resume';
      } else if (filters.success === 'pageview') {
        params.success = 'true';
        params.failureReason = 'Page view';
      } else if (filters.success !== '') {
        params.success = filters.success;
      }
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate)   params.endDate   = filters.endDate;
      const res = await api.get('/users/login-activity', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load login activity');
    } finally { setLoading(false); }
  }, [filters]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <TextField size="small" label="Username" value={filters.username}
            onChange={e => setFilters(p => ({ ...p, username: e.target.value }))}
            placeholder="Search username" sx={{ minWidth: 180 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.success} label="Status"
              onChange={e => setFilters(p => ({ ...p, success: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Success (Login)</MenuItem>
              <MenuItem value="false">Failed</MenuItem>
              <MenuItem value="resume">Session Resume</MenuItem>
              <MenuItem value="pageview">Page View</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="From Date" type="date" value={filters.startDate}
            onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To Date" type="date" value={filters.endDate}
            onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={() => load(1)}>Search</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />}
            onClick={() => { setFilters({ username: '', success: '', startDate: '', endDate: '' }); setLogs([]); setTotal(0); }}>
            Reset
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : logs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">Use filters above and click Search to load logs.</Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{total} total entries</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Page Accessed</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Failure Reason</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>IP Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Device / Browser</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{fmt(log.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{log.username || '—'}</TableCell>
                    <TableCell>
                      {log.failureReason === 'Session resume'
                        ? <Chip label="Session Resume" size="small" color="info" />
                        : log.failureReason?.startsWith('Page view')
                          ? <Chip label={log.failureReason} size="small" color="default" />
                          : log.success
                            ? <Chip icon={<SuccessIcon />} label="Login" size="small" color="success" />
                            : <Chip icon={<FailIcon />} label="Failed" size="small" color="error" />
                      }
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Chip label={pageName(log.currentPage)} size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'error.main' }}>
                      {log.failureReason && !['Session resume'].includes(log.failureReason) && !log.failureReason.startsWith('Page view')
                        ? log.failureReason : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{log.ipAddress || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={log.userAgent || ''}>
                        <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.userAgent || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 50 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination count={Math.ceil(total / 50)} page={page} onChange={(_, p) => load(p)} color="primary" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ── User Changes Tab ──────────────────────────────────────────────────────────
function UserChangesTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ username: '', action: '', changedBy: '', startDate: '', endDate: '' });

  const load = useCallback(async (pg = 1) => {
    setLoading(true); setError('');
    try {
      const params = { page: pg, limit: 50 };
      if (filters.username)  params.username  = filters.username;
      if (filters.action)    params.action    = filters.action;
      if (filters.changedBy) params.changedBy = filters.changedBy;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate)   params.endDate   = filters.endDate;
      const res = await api.get('/users/audit-logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load user audit logs');
    } finally { setLoading(false); }
  }, [filters]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <TextField size="small" label="User Affected" value={filters.username}
            onChange={e => setFilters(p => ({ ...p, username: e.target.value }))}
            placeholder="Username" sx={{ minWidth: 160 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Action</InputLabel>
            <Select value={filters.action} label="Action"
              onChange={e => setFilters(p => ({ ...p, action: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CREATE">Create</MenuItem>
              <MenuItem value="UPDATE">Update</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
              <MenuItem value="STATUS_CHANGE">Status Change</MenuItem>
              <MenuItem value="PASSWORD_RESET">Password Reset</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Changed By" value={filters.changedBy}
            onChange={e => setFilters(p => ({ ...p, changedBy: e.target.value }))}
            placeholder="Username" sx={{ minWidth: 160 }} />
          <TextField size="small" label="From Date" type="date" value={filters.startDate}
            onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To Date" type="date" value={filters.endDate}
            onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={() => load(1)}>Search</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />}
            onClick={() => { setFilters({ username: '', action: '', changedBy: '', startDate: '', endDate: '' }); setLogs([]); setTotal(0); }}>
            Reset
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : logs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">Use filters above and click Search to load logs.</Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{total} total entries</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>User Affected</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Changed By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{fmt(log.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{log.username || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{log.fullName || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" color={ACTION_COLOR[log.action] || 'default'} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{log.changedByName || '—'}</TableCell>
                    <TableCell>
                      <Chip label={log.changedByRole || '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 260 }}>
                      {log.action === 'STATUS_CHANGE'
                        ? <span>
                            <b>{log.oldValues?.isActive ? 'Active' : 'Inactive'}</b>
                            {' → '}
                            <b>{log.newValues?.isActive ? 'Active' : 'Inactive'}</b>
                          </span>
                        : log.changedFields?.length
                          ? <Tooltip title={log.changedFields.join(', ')}>
                              <span>
                                {log.changedFields.slice(0, 3).join(', ')}
                                {log.changedFields.length > 3 ? ` +${log.changedFields.length - 3} more` : ''}
                              </span>
                            </Tooltip>
                          : '—'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 50 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination count={Math.ceil(total / 50)} page={page} onChange={(_, p) => load(p)} color="primary" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserActivityLog() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3, maxWidth: 1300, mx: 'auto' }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <ActivityIcon sx={{ fontSize: 30, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>User Activity Log</Typography>
          <Typography variant="body2" color="text.secondary">Login history and user management changes — owner access only</Typography>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Login Activity" />
        <Tab label="User Changes" />
      </Tabs>

      {tab === 0 && <LoginActivityTab />}
      {tab === 1 && <UserChangesTab />}
    </Box>
  );
}
